import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InkCalibrationCanvas from "../components/InkCalibrationCanvas";
import OnboardingProgressHeader from "../components/OnboardingProgressHeader";
import useInkDeviceProfile from "../hooks/useInkDeviceProfile";
import {
  collectInkEnvironmentSnapshot,
  DEFAULT_PALM_STYLUS_SEPARATION_PX,
  DIAGNOSTIC_CONSENT_VERSION,
  getInkDeviceInstallationId,
  getInkWritingToolLabel,
  INK_WRITING_TOOL_OPTIONS,
  MAX_PALM_STYLUS_SEPARATION_PX,
  MIN_PALM_STYLUS_SEPARATION_PX,
  NEUTRAL_INK_SETTINGS,
  readLocalInkDeviceProfile,
  sanitizeInkSettings,
  saveLocalInkDeviceProfile
} from "../lib/inkDeviceProfile";

const STEPS = ["Device", "Pen", "Palm", "Aim", "Ink feel", "Try it", "Ready"];
const READY_STEP = STEPS.length - 1;

function emptyMetrics() {
  return {
    pointerTypes: [], inputClassification: "Waiting for input", pointerSampleCounts: {}, strokePointerCounts: {},
    sampleCount: 0, coalescedSampleCount: 0, predictedEventCount: 0, hoverSampleCount: 0,
    strokeCount: 0, protectedStrokeCount: 0, rejectedContactCount: 0,
    ignoredContactCounts: {}, pointerCancelCount: 0, ownerCancelCount: 0,
    arbitratedPalmCount: 0, palmReportedFirstCount: 0, ownerTransferCount: 0,
    maxConcurrentContactCount: 0, distinctPointerCount: 0,
    browserLimited: false, verticalZones: {},
    handSeparation: { min: 0, p25: 0, median: 0, p90: 0, max: 0, sampleCount: 0 },
    pressure: { min: 0, max: 0, range: 0 }, contact: { min: 0, median: 0, p90: 0, max: 0 },
    timing: { medianGapMs: 0, p95GapMs: 0, maxGapMs: 0, sampleRateHz: 0 },
    rendering: { medianRenderDelayMs: 0, p95RenderDelayMs: 0, medianTrackingLagPx: 0, p95TrackingLagPx: 0 },
    stylus: { classification: "Waiting for input", hoverSampleCount: 0, predictedEventCount: 0, tiltRange: 0, twistRange: 0, tangentialPressureRange: 0 },
    contactSamples: []
  };
}

function formatMeasurement(value, unit, digits = 1) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? `${parsed.toFixed(digits)} ${unit}` : "Not observed";
}

function formatZoneSignal(metrics, zoneName) {
  const zone = metrics?.verticalZones?.[zoneName];
  if (!zone || !Number(zone.sampleCount || 0)) return "No writing yet";
  const gap = Number(zone.p95GapMs || 0);
  return `${Number(zone.strokeCount || 0)} strokes / ${Number(zone.sampleCount || 0)} points / ${gap > 0 ? `${gap.toFixed(1)} ms gap` : "gap not observed"}`;
}

function deriveLearnedPalmSeparation(metrics, configuredValue) {
  const configured = Number(configuredValue || DEFAULT_PALM_STYLUS_SEPARATION_PX);
  const observed = Number(metrics?.handSeparation?.p25 || metrics?.handSeparation?.median || 0);
  if (!(observed > 0)) return Math.min(configured, DEFAULT_PALM_STYLUS_SEPARATION_PX);
  const learned = Math.round((observed * 0.7) / 10) * 10;
  return Math.max(
    MIN_PALM_STYLUS_SEPARATION_PX,
    Math.min(configured, MAX_PALM_STYLUS_SEPARATION_PX, learned)
  );
}

function getInputClassification(metrics, writingTool) {
  const penStrokes = Number(metrics.strokePointerCounts?.pen || 0);
  const touchStrokes = Number(metrics.strokePointerCounts?.touch || 0);
  if (penStrokes > 0) return "Digital pen connected";
  if (touchStrokes > 0 && writingTool === "active-stylus") return "Battery stylus ready - browser reports touch";
  if (touchStrokes > 0 && writingTool === "capacitive-stylus") return "Passive capacitive stylus ready";
  if (touchStrokes > 0 && writingTool === "finger") return "Touch writing ready";
  if (touchStrokes > 0) return "Touch found - choose your writing tool";
  if (Number(metrics.strokePointerCounts?.mouse || 0) > 0) return "Mouse or trackpad found";
  return "Waiting for your first line";
}

function inferSteadinessChoice(settings) {
  const smoothing = Number(settings.smoothingStrength || 0);
  if (smoothing >= 0.28) return "smooth";
  if (smoothing <= 0.1) return "direct";
  return "balanced";
}

function getResponseLabel(settings) {
  const prediction = Number(settings.predictionMs || 0);
  if (prediction >= 19) return "Strong follow";
  if (prediction >= 10) return "Quick follow";
  if (prediction > 0) return "Gentle follow";
  return "Natural";
}

function deriveObservedSettings(metrics, current) {
  const penStrokeCount = Number(metrics.strokePointerCounts?.pen || 0);
  const completedPenStroke = penStrokeCount > 0;

  // Startup paint delay and one slow sample burst are not stable pen properties.
  // Keep direct ink until the student deliberately tests the feel controls.
  return sanitizeInkSettings({
    ...current,
    inputMode: "auto",
    writingTool: completedPenStroke ? "digital-pen" : current.writingTool,
    minPointDistance: NEUTRAL_INK_SETTINGS.minPointDistance,
    coalescedLimit: Number(metrics.coalescedSampleCount || 0) > 0 ? 96 : NEUTRAL_INK_SETTINGS.coalescedLimit,
    interpolationSegmentLength: 0,
    smoothingStrength: 0,
    velocityResponse: NEUTRAL_INK_SETTINGS.velocityResponse,
    predictionMs: 0
  });
}

function DeviceMetric({ label, value, note }) {
  return <div className="device-metric"><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</div>;
}

function ChallengeHeading({ number, eyebrow, title, children, action }) {
  return (
    <div className="device-stage-heading">
      <div><p className="device-check-stage-number">{eyebrow || `Challenge ${number}`}</p><h2>{title}</h2><p>{children}</p></div>
      {action}
    </div>
  );
}

function TechnicalDetails({ children, label = "Device details" }) {
  return <details className="device-technical-details"><summary>{label}</summary><div className="device-technical-details-grid">{children}</div></details>;
}

function CalibrationExpressions({ instruction }) {
  return (
    <div className="device-calibration-expressions">
      <p>{instruction}</p>
      <div>
        <span className="device-math-expression"><span className="device-math-fraction"><span>3x<sup>2</sup> - 5xy + 7</span><span>2(a + b)</span></span> + &radic;(x<sup>2</sup> + 4)</span>
        <span className="device-math-expression">f(6) = |8x - 9| &le; 10;&nbsp; sin<sup>2</sup>&theta; + cos<sup>2</sup>&theta; = 1</span>
      </div>
    </div>
  );
}

export default function DeviceCheck({ apiBase, token, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = Boolean(
    user && new URLSearchParams(location.search).get("onboarding") === "1"
  );
  const { profile: accountBackedProfile } = useInkDeviceProfile({ apiBase, token });
  const initialLocalProfile = useMemo(() => readLocalInkDeviceProfile(), []);
  const existingProfile = accountBackedProfile || initialLocalProfile;
  const environment = useMemo(() => collectInkEnvironmentSnapshot(), []);
  const installationId = useMemo(() => getInkDeviceInstallationId(), []);
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(false);
  const [probeOpen, setProbeOpen] = useState(false);
  const [mouseMode, setMouseMode] = useState(false);
  const [shareStatus, setShareStatus] = useState("");
  const [settings, setSettings] = useState(() => sanitizeInkSettings(initialLocalProfile?.settings || NEUTRAL_INK_SETTINGS));
  const [capabilityMetrics, setCapabilityMetrics] = useState(emptyMetrics);
  const [baselineMetrics, setBaselineMetrics] = useState(emptyMetrics);
  const [alignmentMetrics, setAlignmentMetrics] = useState(emptyMetrics);
  const [steadyMetrics, setSteadyMetrics] = useState(emptyMetrics);
  const [palmTestMetrics, setPalmTestMetrics] = useState(emptyMetrics);
  const [reviewMetrics, setReviewMetrics] = useState(emptyMetrics);
  const [capabilityClearSignal, setCapabilityClearSignal] = useState(0);
  const [baselineClearSignal, setBaselineClearSignal] = useState(0);
  const [alignmentClearSignal, setAlignmentClearSignal] = useState(0);
  const [steadyClearSignal, setSteadyClearSignal] = useState(0);
  const [palmTestClearSignal, setPalmTestClearSignal] = useState(0);
  const [reviewClearSignal, setReviewClearSignal] = useState(0);
  const [steadinessChoice, setSteadinessChoice] = useState(() => inferSteadinessChoice(settings));
  const [responseMessage, setResponseMessage] = useState("Write the second expression faster and check whether the ink stays with your hand.");
  const [adjustmentNote, setAdjustmentNote] = useState("");
  const [returnToReview, setReturnToReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedProfile, setSavedProfile] = useState(null);
  const [palmDeviceFilteredConfirmed, setPalmDeviceFilteredConfirmed] = useState(false);

  const staticTouchAvailable = Number(environment.maxTouchPoints || 0) > 0 || environment.pointer === "coarse";
  const capabilityPenObserved = Number(capabilityMetrics.strokePointerCounts?.pen || 0) > 0;
  const capabilityTouchObserved = Number(capabilityMetrics.strokePointerCounts?.touch || 0) > 0;
  const capabilityMouseObserved = Number(capabilityMetrics.strokePointerCounts?.mouse || 0) > 0;
  const penObserved = Number(baselineMetrics.strokePointerCounts?.pen || 0) > 0;
  const touchObserved = Number(baselineMetrics.strokePointerCounts?.touch || 0) > 0;
  const digitalPenSignalMismatch = settings.writingTool === "digital-pen"
    && touchObserved
    && !penObserved;
  const touchReportedStylus = ["active-stylus", "capacitive-stylus"].includes(settings.writingTool)
    && touchObserved
    && !penObserved;
  const toolConfirmationNeeded = touchObserved
    && !penObserved
    && (settings.writingTool === "auto-detect" || digitalPenSignalMismatch);
  const baselineReady = baselineMetrics.strokeCount >= 2 && baselineMetrics.sampleCount >= 10 && !toolConfirmationNeeded;
  const successfulPalmStrokes = Math.max(
    0,
    Number(palmTestMetrics.strokeCount || 0) - Number(palmTestMetrics.ownerCancelCount || 0)
  );
  const palmBrowserLimited = palmTestMetrics.browserLimited || Number(palmTestMetrics.ownerCancelCount || 0) > 0;
  const protectedPalmStrokes = Number(palmTestMetrics.protectedStrokeCount || 0);
  const latePalmContacts = Number(palmTestMetrics.ignoredContactCounts?.["late-palm-contact"] || 0);
  const secondaryPalmContacts = Number(palmTestMetrics.ignoredContactCounts?.["secondary-contact"] || 0);
  const spacingRejectedContacts = Number(palmTestMetrics.ignoredContactCounts?.["inside-palm-spacing"] || 0);
  const arbitratedPalmContacts = Number(palmTestMetrics.arbitratedPalmCount || 0);
  const palmReportedFirstCount = Number(palmTestMetrics.palmReportedFirstCount || 0);
  const ownerTransferCount = Number(palmTestMetrics.ownerTransferCount || 0);
  const maxConcurrentPalmContacts = Number(palmTestMetrics.maxConcurrentContactCount || 0);
  const palmPairSeen = maxConcurrentPalmContacts >= 2
    || secondaryPalmContacts > 0
    || arbitratedPalmContacts > 0;
  const palmContactEvidence = palmPairSeen || latePalmContacts > 0
    || Number(palmTestMetrics.handSeparation?.sampleCount || 0) > 0;
  const palmNeedsAdjustment = spacingRejectedContacts > 0 && protectedPalmStrokes < 3;
  const canConfirmDeviceFilteredPalm = successfulPalmStrokes >= 6
    && !palmContactEvidence
    && !palmBrowserLimited;
  const palmTestReady = (palmPairSeen && protectedPalmStrokes >= 3)
    || Number(palmTestMetrics.ownerCancelCount || 0) >= 2
    || palmDeviceFilteredConfirmed;
  const palmSeparationPx = Number(
    settings.palmStylusSeparationPx || DEFAULT_PALM_STYLUS_SEPARATION_PX
  );
  const palmResultClass = palmBrowserLimited
    ? "browser-limited"
    : palmNeedsAdjustment
      ? "needs-adjustment"
      : palmTestReady
        ? "success"
        : "";
  const palmResultTitle = palmBrowserLimited
    ? "This browser stopped the pen signal"
    : palmNeedsAdjustment
      ? "Your pen is too close to the resting hand"
      : palmTestReady
        ? palmDeviceFilteredConfirmed ? "Your device filters the palm" : "Your pen and palm were separated"
        : palmContactEvidence
          ? "Palm found. Continue with your pen"
          : "Write first, then place your palm";
  const palmResultMessage = palmBrowserLimited
    ? `The browser cancelled ${palmTestMetrics.ownerCancelCount} writing ${Number(palmTestMetrics.ownerCancelCount) === 1 ? "stroke" : "strokes"} when another contact appeared. InkSolver cannot recreate points the browser never sent.`
    : palmNeedsAdjustment
      ? `${spacingRejectedContacts} contacts began inside the protected space. Move the distance slider left, clear the slate, and try again.`
      : palmTestReady
        ? palmDeviceFilteredConfirmed
          ? "No additional palm contact reached the browser, and you confirmed that your hand was resting while writing."
          : `${protectedPalmStrokes} pen strokes completed while InkSolver kept the lower hand contact out of the ink.`
        : palmContactEvidence
          ? `We blocked ${latePalmContacts + secondaryPalmContacts + arbitratedPalmContacts} palm ${latePalmContacts + secondaryPalmContacts + arbitratedPalmContacts === 1 ? "contact" : "contacts"}. Keep your hand down and complete three more pen strokes.`
          : "Write a few characters, lift the pen, place your palm down, and continue writing without lifting your hand.";
  const feelReady = steadyMetrics.strokeCount >= 8;
  const reviewReady = reviewMetrics.strokeCount >= 6;
  const reviewOwnerCancellations = Number(reviewMetrics.ownerCancelCount || 0);
  const reviewIgnoredStarts = Number(reviewMetrics.rejectedContactCount || 0);
  const skipPalm = !staticTouchAvailable || mouseMode || penObserved;
  const shortDeviceId = installationId.length > 14 ? `${installationId.slice(0, 8)}...${installationId.slice(-4)}` : installationId;

  useEffect(() => {
    if (step !== 0 || !accountBackedProfile?.settings) return;
    setSettings(sanitizeInkSettings(accountBackedProfile.settings));
    setSteadinessChoice(inferSteadinessChoice(accountBackedProfile.settings));
  }, [accountBackedProfile, step]);

  function goBack() {
    navigate(user ? "/profile" : "/login", isOnboarding ? { state: { fromCalibration: true } } : undefined);
  }
  function continueAfterCalibration() {
    navigate(isOnboarding ? "/solve-first-problem" : user ? "/profile" : "/login", {
      replace: isOnboarding
    });
  }
  function skipCalibration() {
    navigate("/solve-first-problem", {
      replace: true,
      state: { calibrationSkipped: true }
    });
  }
  function updateSetting(key, value) { setSettings((current) => sanitizeInkSettings({ ...current, [key]: value })); }

  function startCalibration({ useMouse = false } = {}) {
    if (!consent) return;
    setMouseMode(useMouse);
    setSettings((current) => sanitizeInkSettings({
      ...current,
      inputMode: "auto",
      writingTool: useMouse
        ? "mouse"
        : capabilityPenObserved
          ? "digital-pen"
          : current.writingTool,
      ...(useMouse ? { palmTouchContactPx: 0, palmHardRejectContactPx: 0, rejectWidePrimaryTouch: false } : {})
    }));
    setStep(1);
  }

  async function shareSetupLink() {
    const url = window.location.href;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: "InkSolver writing setup", url });
        setShareStatus("Link shared");
      } else {
        await navigator.clipboard.writeText(url);
        setShareStatus("Link copied");
      }
    } catch (err) {
      if (err?.name !== "AbortError") setShareStatus("Copy the page address and open it on your touch device");
    }
  }

  function continueFromObservation() {
    setSettings((current) => sanitizeInkSettings({
      ...deriveObservedSettings(baselineMetrics, current),
      palmGuardEnabled: false,
      palmGuardAutoAdvance: false,
      palmCalibrationReady: false,
      palmStylusSeparationPx: DEFAULT_PALM_STYLUS_SEPARATION_PX,
      palmTouchContactPx: 0,
      palmHardRejectContactPx: 0,
      rejectWidePrimaryTouch: false
    }));
    setSteadinessChoice("direct");
    setResponseMessage("Start with direct ink. Then compare the steadier options while writing at normal speed.");
    setStep(skipPalm ? 3 : 2);
  }

  function nudgeInk(axis, amount) {
    setSettings((current) => sanitizeInkSettings({ ...current, [axis]: Number(current[axis] || 0) + amount }));
  }
  function resetAlignment() { setSettings((current) => sanitizeInkSettings({ ...current, nibOffsetX: 0, nibOffsetY: 0 })); }

  function applySteadinessPreset(choice) {
    const presets = {
      direct: { minPointDistance: 0.75, smoothingStrength: 0, velocityResponse: 0.7, predictionMs: 0, interpolationSegmentLength: 0 },
      balanced: { minPointDistance: 0.65, smoothingStrength: 0.18, velocityResponse: 0.74, predictionMs: 0, interpolationSegmentLength: 0 },
      smooth: { minPointDistance: 0.55, smoothingStrength: 0.4, velocityResponse: 0.62, predictionMs: 0, interpolationSegmentLength: 0 }
    };
    setSteadinessChoice(choice);
    setSettings((current) => sanitizeInkSettings({ ...current, ...presets[choice] }));
    setResponseMessage("Write both expressions again to feel this setting at normal and faster speed.");
    setSteadyClearSignal((value) => value + 1);
  }

  function adjustResponse(feedback) {
    setSettings((current) => {
      if (feedback === "behind") {
        setResponseMessage("Moved the ink forward a little. Write the second expression quickly again.");
        return sanitizeInkSettings({ ...current, predictionMs: Math.min(8, Number(current.predictionMs || 0) + 3), velocityResponse: Number(current.velocityResponse || 0.7) + 0.03 });
      }
      if (feedback === "ahead") {
        setResponseMessage("Pulled the ink back a little. Write the second expression quickly again.");
        return sanitizeInkSettings({ ...current, predictionMs: Number(current.predictionMs || 0) - 3 });
      }
      if (feedback === "direct") {
        setSteadinessChoice("direct");
        setResponseMessage("Restored direct ink with no prediction or gap repair.");
        return sanitizeInkSettings({
          ...current,
          minPointDistance: 0.75,
          smoothingStrength: 0,
          velocityResponse: 0.7,
          predictionMs: 0,
          interpolationSegmentLength: 0
        });
      }
      setResponseMessage("Added gentle gap repair. Write the second expression quickly again.");
      return sanitizeInkSettings({ ...current, interpolationSegmentLength: Math.min(2.4, Math.max(1.2, Number(current.interpolationSegmentLength || 0) + 0.6)), coalescedLimit: Math.max(96, Number(current.coalescedLimit || 64)) });
    });
    setSteadyClearSignal((value) => value + 1);
  }

  function finishChallenge(nextStep) {
    if (returnToReview) {
      setReturnToReview(false);
      setAdjustmentNote("Setting updated. Write the math steps again and check the feel.");
      setReviewClearSignal((value) => value + 1);
      setStep(5);
      return;
    }
    setAdjustmentNote("");
    setStep(nextStep);
  }

  function resetPalmCalibration() {
    setPalmDeviceFilteredConfirmed(false);
    setSettings((current) => sanitizeInkSettings({
      ...current,
      palmGuardEnabled: false,
      palmGuardAutoAdvance: false,
      palmCalibrationReady: false,
      palmStylusSeparationPx: DEFAULT_PALM_STYLUS_SEPARATION_PX
    }));
    setPalmTestClearSignal((value) => value + 1);
  }

  function updatePalmSeparation(value) {
    setPalmDeviceFilteredConfirmed(false);
    updateSetting("palmStylusSeparationPx", Number(value));
    setPalmTestClearSignal((current) => current + 1);
  }

  function continueFromPalm() {
    const savedSeparation = deriveLearnedPalmSeparation(palmTestMetrics, palmSeparationPx);
    setSettings((current) => sanitizeInkSettings({
      ...current,
      palmGuardEnabled: false,
      palmGuardAutoAdvance: false,
      palmCalibrationReady: true,
      palmStylusSeparationPx: savedSeparation
    }));
    const note = palmBrowserLimited
      ? "This browser stopped the writing pointer when your palm landed. We saved the spacing, but cannot restore points the browser does not send."
      : `Palm protection saved at ${savedSeparation.toFixed(0)} px. Later writing tests will reuse the faster calibrated handoff instead of learning your palm again.`;
    if (returnToReview) {
      setReturnToReview(false);
      setAdjustmentNote(`${note} Write the math steps again and check the feel.`);
      setReviewClearSignal((value) => value + 1);
      setStep(5);
      return;
    }
    setAdjustmentNote(note);
    setStep(3);
  }

  function routeReviewFeedback(reason) {
    const routes = {
      alignment: [3, "Let us put the ink directly under your pen."],
      shaky: [4, "Let us make the line steadier without making it heavy."],
      width: [4, "Choose a pen thickness that feels natural."],
      behind: [4, "Let us help the ink keep up with faster writing."],
      missing: [4, "Let us repair the small gaps between fast samples."],
      palm: [2, "Let us measure your natural pen-to-hand spacing again."]
    };
    const [nextStep, note] = routes[reason];
    if (reason === "palm") {
      resetPalmCalibration();
    }
    setReturnToReview(true);
    setAdjustmentNote(note);
    setStep(nextStep);
  }

  async function saveCalibration() {
    setSaving(true);
    setSaveError("");
    const now = new Date().toISOString();
    const writingMetrics = [baselineMetrics, palmTestMetrics, alignmentMetrics, steadyMetrics, reviewMetrics];
    const pointerTypes = [...new Set(writingMetrics.flatMap((metrics) => metrics.pointerTypes || []))];
    const strokePointerCounts = writingMetrics.reduce((result, metrics) => {
      Object.entries(metrics.strokePointerCounts || {}).forEach(([type, count]) => { result[type] = Number(result[type] || 0) + Number(count || 0); });
      return result;
    }, {});
    const inputClassification = getInputClassification(baselineMetrics, settings.writingTool);
    const diagnosticSummary = {
      pointerTypes, writingTool: settings.writingTool, strokePointerCounts,
      sampleCount: writingMetrics.reduce((sum, metrics) => sum + Number(metrics.sampleCount || 0), 0),
      strokeCount: writingMetrics.reduce((sum, metrics) => sum + Number(metrics.strokeCount || 0), 0),
      coalescedSampleCount: writingMetrics.reduce((sum, metrics) => sum + Number(metrics.coalescedSampleCount || 0), 0),
      rejectedContactCount: Number(palmTestMetrics.rejectedContactCount || 0)
        + Number(reviewMetrics.rejectedContactCount || 0),
      pressure: baselineMetrics.pressure, contact: palmTestMetrics.contact, timing: baselineMetrics.timing,
      handSeparation: palmTestMetrics.handSeparation,
      palmCompatibility: {
        status: skipPalm ? "not-tested" : palmBrowserLimited ? "browser-limited" : "supported",
        configuredSeparationPx: palmSeparationPx,
        protectedStrokeCount: protectedPalmStrokes,
        arbitratedPalmCount: arbitratedPalmContacts,
        palmReportedFirstCount,
        ownerTransferCount,
        maxConcurrentContactCount: maxConcurrentPalmContacts,
        latePalmContactCount: latePalmContacts,
        secondaryContactCount: secondaryPalmContacts,
        spacingRejectedContactCount: spacingRejectedContacts,
        pointerCancelCount: Number(palmTestMetrics.pointerCancelCount || 0),
        ownerCancelCount: Number(palmTestMetrics.ownerCancelCount || 0)
      },
      rendering: { ...baselineMetrics.rendering, sampleRateHz: baselineMetrics.timing.sampleRateHz },
      verticalZones: reviewMetrics.verticalZones,
      stylus: { ...baselineMetrics.stylus, classification: inputClassification },
      completedStages: skipPalm ? ["device", "pen", "aim", "ink-feel", "review"] : ["device", "pen", "palm", "aim", "ink-feel", "review"]
    };
    const localProfile = saveLocalInkDeviceProfile({
      installationId, label: `${environment.platform || "Browser"} writing device`, environment, settings, diagnosticSummary,
      consent: { version: DIAGNOSTIC_CONSENT_VERSION, accepted: true, acceptedAt: now },
      calibratedAt: now, updatedAt: now, source: token ? "local-pending-sync" : "local"
    });
    if (!token) { setSavedProfile(localProfile); setSaving(false); setStep(READY_STEP); return; }
    try {
      const response = await fetch(`${apiBase}/api/me/ink-device-profiles/current`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ installationId, label: localProfile.label, environment, settings, diagnosticSummary, consent: { version: DIAGNOSTIC_CONSENT_VERSION, accepted: true } })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Could not sync this device profile");
      const synced = saveLocalInkDeviceProfile({ ...data.profile, installationId, source: "account" });
      setSavedProfile(synced); setStep(READY_STEP);
    } catch (err) {
      setSavedProfile(localProfile); setSaveError(`${err.message}. The calibration is still saved in this browser.`); setStep(READY_STEP);
    } finally { setSaving(false); }
  }

  function restartSetup() {
    [setCapabilityClearSignal, setBaselineClearSignal, setPalmTestClearSignal, setAlignmentClearSignal, setSteadyClearSignal, setReviewClearSignal]
      .forEach((setter) => setter((value) => value + 1));
    setAdjustmentNote(""); setReturnToReview(false); setPalmDeviceFilteredConfirmed(false); setStep(0);
  }

  return (
    <main className="device-check-page">
      <div className="device-check-paper-grain" aria-hidden="true" />
      {isOnboarding ? (
        <OnboardingProgressHeader currentStep="calibration">
          <button type="button" className="secondary-button" onClick={goBack}>Back to profile</button>
          <button type="button" className="secondary-button onboarding-skip-button" onClick={skipCalibration}>Skip for now</button>
        </OnboardingProgressHeader>
      ) : (
        <header className="device-check-header">
          <button type="button" className="device-check-back" onClick={goBack} aria-label="Leave device check"><span aria-hidden="true">&#8592;</span><span>{user ? "Profile" : "Login"}</span></button>
          <div className="device-check-brand"><span className="device-check-brand-mark">i</span><span>InkSolver</span></div>
          <div className="device-check-id">Device {shortDeviceId}</div>
        </header>
      )}
      <section className="device-check-shell">
        <div className="device-check-intro">
          <p className="device-check-kicker">Writing device setup</p>
          <h1>Calibrate your writing with a <em>stylus.</em></h1>
          <p>Complete a few quick writing challenges. InkSolver will remember what feels right on this device.</p>
        </div>
        <ol className="device-check-progress" aria-label="Device setup progress">
          {STEPS.map((label, index) => <li key={label} className={`${index === step ? "active" : ""} ${index < step ? "complete" : ""}`.trim()}><span>{index < step ? "OK" : index + 1}</span><strong>{label}</strong></li>)}
        </ol>

        {step === 0 && (
          <div className="device-check-stage readiness-stage">
            <ChallengeHeading eyebrow="Device readiness" title="Can this device write?">We check for touch or a connected pen before opening the writing challenges.</ChallengeHeading>
            <div className={`device-readiness-result ${staticTouchAvailable ? "is-ready" : "needs-device"}`}>
              <span className="device-readiness-icon" aria-hidden="true">{staticTouchAvailable ? "OK" : "!"}</span>
              <div><strong>{staticTouchAvailable ? "Touch writing is available" : "Touch writing is not available"}</strong><p>{staticTouchAvailable ? "This device is ready for a stylus or finger setup." : "Test a connected drawing pen, continue with a mouse, or open this page on a touchscreen device."}</p></div>
            </div>
            {existingProfile?.calibratedAt && (
              <details className="device-current-setup">
                <summary>Current saved setup</summary>
                <div className="device-ready-summary student-summary">
                  <DeviceMetric label="Writing tool" value={getInkWritingToolLabel(settings.writingTool)} />
                  <DeviceMetric label="Pen thickness" value={`${settings.lineWidth.toFixed(2)} px`} />
                  <DeviceMetric label="Aim" value={settings.nibOffsetX || settings.nibOffsetY ? "Adjusted" : "Centered"} />
                  <DeviceMetric label="Ink smoothing" value={`${Math.round(settings.smoothingStrength * 100)}%`} />
                  <DeviceMetric label="Point spacing" value={`${settings.minPointDistance.toFixed(2)} px`} />
                  <DeviceMetric label="Palm separation" value={settings.palmCalibrationReady ? `${settings.palmStylusSeparationPx.toFixed(0)} px` : "Not calibrated"} />
                </div>
              </details>
            )}
            <label className="device-consent-check compact"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} /><span>I agree to let InkSolver observe writing signals and save this device setup.</span></label>
            {!staticTouchAvailable && (
              <div className="device-no-touch-options">
                <button type="button" className="device-secondary-cta" disabled={!consent} onClick={() => setProbeOpen(true)}>Test a connected pen</button>
                <button type="button" className="device-secondary-cta" disabled={!consent} onClick={() => startCalibration({ useMouse: true })}>Continue with mouse</button>
                <button type="button" className="device-secondary-cta" onClick={shareSetupLink}>Open on another device</button>
                {shareStatus && <small>{shareStatus}</small>}
              </div>
            )}
            {probeOpen && !staticTouchAvailable && (
              <div className="device-pen-probe">
                <div className="device-challenge-callout"><strong>Draw one short line with the connected pen.</strong><span>A mouse click does not count as a pen test.</span></div>
                <InkCalibrationCanvas settings={NEUTRAL_INK_SETTINGS} onMetricsChange={setCapabilityMetrics} clearSignal={capabilityClearSignal} className="device-capability-probe" />
                <div className={`device-probe-result ${capabilityPenObserved || capabilityTouchObserved ? "success" : ""}`}>{capabilityPenObserved || capabilityTouchObserved ? "Writing input found. You can continue." : capabilityMouseObserved ? "Only a mouse or trackpad was found. Try again with the connected pen." : "Waiting for a pen line..."}</div>
              </div>
            )}
            <TechnicalDetails label="What did the browser find?"><DeviceMetric label="Platform" value={environment.platform || "Not exposed"} /><DeviceMetric label="Browser" value={environment.browser || "Not exposed"} /><DeviceMetric label="Touch points" value={String(environment.maxTouchPoints || 0)} /><DeviceMetric label="Pointer" value={environment.pointer || "unknown"} /></TechnicalDetails>
            <div className="device-check-actions split">
              <p>{!consent ? "Confirm the device check to continue." : staticTouchAvailable || capabilityPenObserved || capabilityTouchObserved ? "Your writing surface is ready." : "Connect a pen or choose another option."}</p>
              {(staticTouchAvailable || capabilityPenObserved || capabilityTouchObserved) && <button type="button" className="device-primary-cta" disabled={!consent} onClick={() => startCalibration()}>Start the challenges <span aria-hidden="true">&#8594;</span></button>}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="device-check-stage writing-stage">
            <ChallengeHeading number="1 of 5" title="Meet your pen." action={<button type="button" className="device-quiet-button" onClick={() => setBaselineClearSignal((value) => value + 1)}>Clear</button>}>Tell us what you are holding, then write the expression twice. We will quietly learn how the browser receives it.</ChallengeHeading>
            {!mouseMode && <div className="device-writing-tool-confirmation student-choice"><div><strong>What are you writing with?</strong><span>Choose the tip in your hand. We will also check what signal the browser actually sends.</span></div><div className="device-writing-tool-options" role="group" aria-label="Primary writing tool">{INK_WRITING_TOOL_OPTIONS.map((option) => <button key={option.value} type="button" className={(settings.writingTool === option.value || (penObserved && option.value === "digital-pen")) ? "active" : ""} onClick={() => updateSetting("writingTool", option.value)}><strong>{option.label}</strong><span>{option.description}</span></button>)}</div>{penObserved && <small className="device-digital-pen-signal">Digital pen signal detected. Your device handles pressure and palm distinction; InkSolver will calibrate your aim and ink feel without asking for a separate palm-distance test.</small>}{digitalPenSignalMismatch && <small className="device-tool-mismatch">This browser reported touch, not a separate digital pen. If the stylus has a battery and metal/plastic tip, choose Active Stylus. Otherwise choose Capacitive Stylus.</small>}</div>}
            <div className="device-writing-prompt"><span>Write twice</span><strong>S<sub>n</sub> = 9n + 2n<sup>2</sup></strong><small>Write as naturally as you would in a notebook.</small></div>
            <InkCalibrationCanvas settings={NEUTRAL_INK_SETTINGS} onMetricsChange={setBaselineMetrics} clearSignal={baselineClearSignal} />
            <div className={`device-challenge-result ${baselineReady ? "success" : ""}`}><span aria-hidden="true">{baselineReady ? "OK" : baselineMetrics.strokeCount}</span><div><strong>{getInputClassification(baselineMetrics, settings.writingTool)}</strong><p>{toolConfirmationNeeded ? "Choose the writing tool above so we tune the correct experience." : baselineReady ? "Signal captured. Your next challenge is accuracy." : "Complete two natural lines to continue."}</p></div></div>
            <TechnicalDetails><DeviceMetric label="Browser signals" value={Object.entries(baselineMetrics.pointerSampleCounts).map(([type, count]) => `${type} ${count}`).join(" + ") || "Waiting"} /><DeviceMetric label="Sample rate" value={formatMeasurement(baselineMetrics.timing.sampleRateHz, "Hz", 0)} /><DeviceMetric label="Sample gap" value={formatMeasurement(baselineMetrics.timing.p95GapMs, "ms")} /><DeviceMetric label="Render delay" value={formatMeasurement(baselineMetrics.rendering.p95RenderDelayMs, "ms")} /></TechnicalDetails>
            <div className="device-check-actions split"><button type="button" className="device-secondary-cta" onClick={() => setStep(0)}>Back</button><button type="button" className="device-primary-cta" disabled={!baselineReady} onClick={continueFromObservation}>{skipPalm ? "Aim challenge" : "Set up my palm"} <span aria-hidden="true">&#8594;</span></button></div>
          </div>
        )}

        {step === 2 && (
          <div className="device-check-stage palm-stage">
            <ChallengeHeading number="2 of 5" title="Write with your hand in its natural position." action={<button type="button" className="device-quiet-button" onClick={resetPalmCalibration}>Reset</button>}>Rest your hand as you normally would and write continuously. InkSolver will separate the upper writing contact from the lower hand contact even when the browser reports both as touch.</ChallengeHeading>
            {adjustmentNote && <div className="device-adjustment-note">{adjustmentNote}</div>}
            {touchReportedStylus && <div className="device-adjustment-note">Your browser calls both your pen and hand "touch". Ink starts immediately; if the lower hand arrives first, InkSolver hands control to the upper writing contact.</div>}
            <div className="device-palm-writing-test">
              <div className="device-challenge-callout"><strong>Keep your palm resting.</strong><span>Write x<sup>2</sup> + 5x + 6 = 0, then continue with another line without changing your normal grip.</span></div>
              <label className="device-range-control palm-distance-control">
                <span><strong>Palm detection distance</strong><output>{palmSeparationPx.toFixed(0)} px</output></span>
                <input
                  type="range"
                  min={MIN_PALM_STYLUS_SEPARATION_PX}
                  max={MAX_PALM_STYLUS_SEPARATION_PX}
                  step="10"
                  value={palmSeparationPx}
                  onChange={(event) => updatePalmSeparation(event.target.value)}
                />
                <small>This recognizes a new palm below recent writing. Once the palm is known, later pen strokes use a much smaller clearance so changing lines does not stop the ink.</small>
              </label>
              <InkCalibrationCanvas settings={settings} onMetricsChange={setPalmTestMetrics} clearSignal={palmTestClearSignal} className="compact palm-spacing-test" />
              <div className={`device-challenge-result ${palmResultClass}`}><span aria-hidden="true">{palmBrowserLimited || palmNeedsAdjustment ? "!" : palmTestReady ? "OK" : protectedPalmStrokes}</span><div><strong>{palmResultTitle}</strong><p>{palmResultMessage}</p></div></div>
              {canConfirmDeviceFilteredPalm && !palmDeviceFilteredConfirmed && <div className="device-filtered-palm-confirm"><span>No second contact reached this browser.</span><button type="button" onClick={() => setPalmDeviceFilteredConfirmed(true)}>My palm is resting and writing works</button></div>}
              <TechnicalDetails label="Palm test details"><DeviceMetric label="Pen + hand seen together" value={palmPairSeen ? "Yes" : "No"} /><DeviceMetric label="Protected pen strokes" value={String(protectedPalmStrokes)} /><DeviceMetric label="Palm contacts blocked" value={String(latePalmContacts + secondaryPalmContacts + arbitratedPalmContacts)} /><DeviceMetric label="Palm-first handoffs" value={String(ownerTransferCount)} /><DeviceMetric label="Pen cancellations" value={String(palmTestMetrics.ownerCancelCount || 0)} /><DeviceMetric label="Top third signal" value={formatZoneSignal(palmTestMetrics, "top")} /><DeviceMetric label="Middle third signal" value={formatZoneSignal(palmTestMetrics, "middle")} /><DeviceMetric label="Bottom third signal" value={formatZoneSignal(palmTestMetrics, "bottom")} /></TechnicalDetails>
              <div className="device-check-actions split"><button type="button" className="device-secondary-cta" onClick={() => setStep(1)}>Back</button><button type="button" className="device-primary-cta" disabled={!palmTestReady} onClick={continueFromPalm}>{palmBrowserLimited ? "Continue with browser limit" : "Use this protection"} <span aria-hidden="true">&#8594;</span></button></div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="device-check-stage alignment-stage">
            <ChallengeHeading number="3 of 5" title="Put the ink under your pen." action={<button type="button" className="device-quiet-button" onClick={() => setAlignmentClearSignal((value) => value + 1)}>Clear</button>}>Slowly trace the blue crosshair. If the ink misses your pen tip, nudge it in the direction it needs to move.</ChallengeHeading>
            {adjustmentNote && <div className="device-adjustment-note">{adjustmentNote}</div>}
            <InkCalibrationCanvas settings={settings} onMetricsChange={setAlignmentMetrics} clearSignal={alignmentClearSignal} guide="alignment" className="compact" />
            <div className="device-nib-controls"><div><strong>Move the ink</strong><span>Use small taps while watching your last crosshair trace.</span></div><div className="device-nudge-pad" role="group" aria-label="Move ink under the pen tip"><button type="button" className="up" onClick={() => nudgeInk("nibOffsetY", -1)}>Up</button><button type="button" className="left" onClick={() => nudgeInk("nibOffsetX", -1)}>Left</button><button type="button" className="reset" onClick={resetAlignment}>Reset</button><button type="button" className="right" onClick={() => nudgeInk("nibOffsetX", 1)}>Right</button><button type="button" className="down" onClick={() => nudgeInk("nibOffsetY", 1)}>Down</button></div></div>
            <TechnicalDetails label="Advanced alignment values"><DeviceMetric label="Horizontal" value={`${settings.nibOffsetX.toFixed(1)} px`} /><DeviceMetric label="Vertical" value={`${settings.nibOffsetY.toFixed(1)} px`} /></TechnicalDetails>
            <div className="device-check-actions split"><button type="button" className="device-secondary-cta" onClick={() => setStep(skipPalm ? 1 : 2)}>Back</button><button type="button" className="device-primary-cta" onClick={() => finishChallenge(4)}>Tune handwriting <span aria-hidden="true">&#8594;</span></button></div>
          </div>
        )}

        {step === 4 && (
          <div className="device-check-stage steady-stage">
            <ChallengeHeading number="4 of 5" title="Make fast math feel natural." action={<button type="button" className="device-quiet-button" onClick={() => setSteadyClearSignal((value) => value + 1)}>Clear</button>}>Choose an ink feel, write the first expression normally, then write the second a little faster. {palmBrowserLimited ? "Keep your hand lifted because this browser cancelled the pen during the palm test." : "Keep your hand in the same natural writing position."}</ChallengeHeading>
            {adjustmentNote && <div className="device-adjustment-note">{adjustmentNote}</div>}
            <div className="device-feel-choices">{[["direct", "Direct", "Follows the points exactly"], ["balanced", "Steady", "Lightly settles shaky lines"], ["smooth", "Smooth", "Stronger help for fast writing"]].map(([value, label, note]) => <button key={value} type="button" className={steadinessChoice === value ? "active" : ""} onClick={() => applySteadinessPreset(value)}><strong>{label}</strong><span>{note}</span></button>)}</div>
            <CalibrationExpressions instruction="Copy both expressions. Write the first normally and the second faster." />
            <InkCalibrationCanvas settings={settings} onMetricsChange={setSteadyMetrics} clearSignal={steadyClearSignal} className="math-writing" />
            <div className="device-response-panel"><div><strong>{getResponseLabel(settings)}</strong><span>{responseMessage}</span></div><div className="device-symptom-actions"><button type="button" onClick={() => adjustResponse("behind")}>Ink is behind</button><button type="button" onClick={() => adjustResponse("ahead")}>Ink jumps ahead</button><button type="button" onClick={() => adjustResponse("missing")}>Parts are missing</button><button type="button" onClick={() => adjustResponse("direct")}>Reset direct ink</button></div></div>
            <div className="device-pen-width-choice"><strong>Choose your pen thickness</strong><div role="group" aria-label="Ink thickness">{[[1.75, "Thin"], [2.5, "Regular"], [3.5, "Bold"]].map(([width, label]) => <button type="button" key={label} className={Math.abs(settings.lineWidth - width) < 0.2 ? "active" : ""} onClick={() => updateSetting("lineWidth", width)}><span style={{ height: `${width}px` }} aria-hidden="true" /> {label}</button>)}</div></div>
            <TechnicalDetails label="Advanced ink values"><DeviceMetric label="Point spacing" value={`${settings.minPointDistance.toFixed(2)} px`} /><DeviceMetric label="Stabilization" value={`${Math.round(settings.smoothingStrength * 100)}%`} /><DeviceMetric label="Fast response" value={`${Math.round(settings.velocityResponse * 100)}%`} /><DeviceMetric label="Latency lead" value={`${settings.predictionMs.toFixed(0)} ms`} /><DeviceMetric label="Gap repair" value={settings.interpolationSegmentLength ? `${settings.interpolationSegmentLength.toFixed(1)} px` : "Off"} /><DeviceMetric label="Top third signal" value={formatZoneSignal(steadyMetrics, "top")} /><DeviceMetric label="Middle third signal" value={formatZoneSignal(steadyMetrics, "middle")} /><DeviceMetric label="Bottom third signal" value={formatZoneSignal(steadyMetrics, "bottom")} /></TechnicalDetails>
            <div className="device-check-actions split"><button type="button" className="device-secondary-cta" onClick={() => setStep(3)}>Back</button><button type="button" className="device-primary-cta" disabled={!feelReady} onClick={() => finishChallenge(5)}>This feels natural <span aria-hidden="true">&#8594;</span></button></div>
          </div>
        )}

        {step === 5 && (
          <div className="device-check-stage review-stage">
            <ChallengeHeading number="5 of 5" title="Now write like it is your notebook." action={<button type="button" className="device-quiet-button" onClick={() => setReviewClearSignal((value) => value + 1)}>Clear</button>}>{palmBrowserLimited ? "Write these connected steps with your hand lifted, then give the slate your final verdict." : "Rest your palm naturally and write these connected steps at your normal speed."}</ChallengeHeading>
            {adjustmentNote && <div className="device-adjustment-note success">{adjustmentNote}</div>}
            <div className="device-final-math-prompt" aria-label="Math steps to copy"><span>2x<sup>2</sup> + 7x + 3 = 0</span><span>(2x + 1)(x + 3) = 0</span><span>x = -1/2 or x = -3</span></div>
            <InkCalibrationCanvas settings={settings} onMetricsChange={setReviewMetrics} clearSignal={reviewClearSignal} />
            {reviewOwnerCancellations > 0 && <div className="device-challenge-result browser-limited"><span aria-hidden="true">!</span><div><strong>The browser stopped part of your writing</strong><p>It cancelled {reviewOwnerCancellations} active {reviewOwnerCancellations === 1 ? "stroke" : "strokes"}. Changing smoothness cannot restore points that the browser did not send.</p></div></div>}
            <TechnicalDetails label="Final writing signal"><DeviceMetric label="Completed strokes" value={String(reviewMetrics.strokeCount || 0)} /><DeviceMetric label="Cancelled strokes" value={String(reviewOwnerCancellations)} /><DeviceMetric label="Ignored starts" value={String(reviewIgnoredStarts)} /><DeviceMetric label="Sample gap" value={formatMeasurement(reviewMetrics.timing?.p95GapMs, "ms")} /><DeviceMetric label="Top third signal" value={formatZoneSignal(reviewMetrics, "top")} /><DeviceMetric label="Middle third signal" value={formatZoneSignal(reviewMetrics, "middle")} /><DeviceMetric label="Bottom third signal" value={formatZoneSignal(reviewMetrics, "bottom")} /></TechnicalDetails>
            <div className="device-review-question"><div><p className="device-check-stage-number">Your verdict</p><h3>How does the slate feel?</h3><span>{reviewReady ? "Choose what you notice. We will open only the setting that can fix it." : "Write the three math steps before choosing."}</span></div><button type="button" className="device-primary-cta" disabled={!reviewReady || saving} onClick={saveCalibration}>{saving ? "Saving..." : "Looks good - save it"} <span aria-hidden="true">&#10003;</span></button></div>
            <div className="device-review-feedback-grid"><button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("alignment")}>Ink is away from my pen</button><button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("shaky")}>Ink looks shaky</button><button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("behind")}>Ink trails behind</button><button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("missing")}>Parts of strokes disappear</button>{!skipPalm && <button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("palm")}>My palm creates marks</button>}<button type="button" disabled={!reviewReady} onClick={() => routeReviewFeedback("width")}>Ink is too thick or thin</button></div>
            <div className="device-check-actions split"><button type="button" className="device-secondary-cta" onClick={() => setStep(4)}>Back</button><p>{reviewReady ? "Your setup is ready to save." : "Keep writing naturally for a few more strokes."}</p></div>
          </div>
        )}

        {step === READY_STEP && (
          <div className="device-check-stage ready-stage">
            <div className="device-ready-mark" aria-hidden="true">&#10003;</div><p className="device-check-stage-number">Setup complete</p><h2>Your slate is ready.</h2>
            <p>InkSolver will use this writing feel on this device in Practice and Ink Together. You can return here whenever your pen, browser, or writing feel changes.</p>
            {saveError && <div className="device-save-warning">{saveError}</div>}
            <div className="device-ready-summary student-summary"><DeviceMetric label="Writing tool" value={getInkWritingToolLabel(settings.writingTool)} /><DeviceMetric label="Aim" value={settings.nibOffsetX || settings.nibOffsetY ? "Adjusted" : "Centered"} /><DeviceMetric label="Ink feel" value={steadinessChoice} /><DeviceMetric label="Response" value={getResponseLabel(settings)} /><DeviceMetric label="Palm protection" value={penObserved ? "Handled by digital pen signal" : skipPalm ? "Not needed" : palmBrowserLimited ? "Browser limited" : `${palmSeparationPx.toFixed(0)} px spacing`} /><DeviceMetric label="Saved" value={token && savedProfile?.source === "account" ? "Account + device" : "This device"} /></div>
            <div className="device-check-actions centered"><button type="button" className="device-secondary-cta" onClick={restartSetup}>Edit this setup</button><button type="button" className="device-primary-cta" onClick={continueAfterCalibration}>{isOnboarding ? "Try my first problem" : user ? "Back to profile" : "Continue to login"} <span aria-hidden="true">&#8594;</span></button></div>
          </div>
        )}
      </section>
    </main>
  );
}
